import { AlertTriangle, CheckCircle, Clock, LucideIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IMyProjectList } from '../../../project/core/_models';
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext';
import { Badge, Button, Col, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { getMyprojects } from '../../../project';
import { ApiResponse, IDashboardFilterData, Ipagination } from '../../../core/_models';
import { Link, useLocation } from 'react-router-dom';
import { usePermission } from '../../../auth';
import PermissionGate from '../../../apps/components/UI/PermissionGate';
import SkeletonCard from '../../../apps/components/skeleton/SkeletonCard';
import { getApiErrorMessage } from "../../../../../utils/utils";
import { isAbortedError } from "../../../../../utils/global";
interface EvaluatorGridViewProps {
  search: string;
  filters: IDashboardFilterData;
}
const evalStatusIcons: Record<string, LucideIcon> = {
  overdue: AlertTriangle,
  "in progress": Clock,
  completed: CheckCircle,
};

interface MyProjectsAPIResponse extends ApiResponse<{
  project_data: IMyProjectList[];
  pagination?: Ipagination;
  project_pending?: number;
  project_completed?: number;
  project_evaluated?: number;
}> { }
const getEvalStatusIcon = (evalStatus?: string) => evalStatusIcons[evalStatus?.toLowerCase() || ""] || AlertTriangle;

const priorityIcons: Record<string, LucideIcon> = {
  "long term 15-30 yrs": AlertTriangle,
  "medium term 5-15 yrs": Clock,
  "short term 1-5 yrs": CheckCircle,
};

const getPriorityIcon = (priority?: string): LucideIcon => priorityIcons[priority?.toLowerCase() || ""] || AlertTriangle;

const gradients = {
  topBar: "linear-gradient(90deg, #1e4780 0%, #4a8fe0 50%, #5a9ff0 100%)",
  badge: "linear-gradient(135deg, #3c72e7 0%, #4a8fe0 100%)",
  iconBg: "linear-gradient(135deg, #3c72e7 0%, #2b4d75 100%)",
  lightBlue: "linear-gradient(145deg, #e8f1fe 0%, #d6e4ff 100%)",
  lightSuccess: "linear-gradient(145deg, #e8f8f0 0%, #d4f7df 100%)",
  lightWarning: "linear-gradient(145deg, #fff8e6 0%, #ffe8b3 100%)",
  lightDanger: "linear-gradient(145deg, #fdecec 0%, #ffd6d6 100%)",
  success: "#229150",
  warning: "#f39c12",
  danger: "#aa2517",
  info: "#3498db",
};

const evalStatusBg: Record<string, string> = {
  overdue: gradients.lightDanger,
  "in progress": gradients.lightWarning,
  completed: gradients.lightSuccess,
};

const getEvalStatusBg = (evalStatus?: string) => evalStatusBg[evalStatus?.toLowerCase() || ""] || gradients.lightBlue;

const evalStatusColor: Record<string, string> = {
  overdue: gradients.danger,
  "in progress": gradients.warning,
  completed: gradients.success,
}

const getEvalStatusColor = (evalStatus?: string) => evalStatusColor[evalStatus?.toLowerCase() || ""] || gradients.info

const InfoItem = ({ icon, value }: { icon: string, value: string | number }) => (
  <div className="d-flex align-items-center gap-2 flex-grow-1 w-50" style={{ minWidth: 0 }}>
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{
        background: gradients.lightBlue,
        width: "30px",
        height: "30px"
      }}
    >
      <i className={icon} style={{ color: "#3a7bd5", fontSize: "1.2rem" }}></i>
    </div>
    <div className="d-flex flex-column min-w-0">
      <span className="fw-semibold  fs-3">
        {value}
      </span>
    </div>
  </div>
)

const EvaluatorGridView: FC<EvaluatorGridViewProps> = ({ search, filters }) => {
  const location = useLocation();
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const [listing, setListing] = useState<IMyProjectList[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const ITEMS_PER_PAGE = 12;

  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Refs for performance optimization
  const isFirstRender = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const hasMoreProjects = listing.length < totalRecords;
  const loadedProjectCount = listing.length;


  const fetchListing = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const listingResponse = await getMyprojects(
        filters,
        page,
        ITEMS_PER_PAGE,
        search,
        true,
        controller.signal
      ) as MyProjectsAPIResponse;

      if (controller.signal.aborted) return;

      if (listingResponse.status === "ok") {
        const data = listingResponse.data;
        if (page === 1) {
          setListing(data.project_data || []);
        } else {
          setListing((prevProjects) => [...prevProjects, ...data.project_data || []]);
        }
        setTotalRecords(data?.pagination?.total_records || data.project_data.length || 0);

      } else {
        setListing([]);
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch project listing:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch project listing"), "danger");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters, showSnackbar, page, search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchListing();
    }, isFirstRender.current ? 100 : 0);

    isFirstRender.current = false;

    return () => {
      clearTimeout(timeoutId);
      abortControllerRef.current?.abort();
    };
  }, [fetchListing]);

  const renderLoadMoreButton = useCallback(() => {
    if (!hasMoreProjects) return null;

    return (
      <div className="d-flex flex-column align-items-center mt-4 gap-2">
        <div className="text-gray-700">
          Showing {loadedProjectCount} of {totalRecords} projects
        </div>
        <Button
          className="btn-primary"
          onClick={() => setPage((prev) => prev + 1)}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Loading...
            </>
          ) : (
            'Load more projects'
          )}
        </Button>
      </div>
    );
  }, [hasMoreProjects, loadedProjectCount, totalRecords, loading]);

  return (
    <>
      <Row className="g-4">
        {loading ? (
          Array.from({ length: 9 }).map((_, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-4">
              <SkeletonCard
                wrapperClassName=""
                className="mb-4"
                titleWidth="75%"
                valueWidth="75%"
                showIcon={false}
                lines={4}
              />
            </div>
          ))
        ) : (listing.length > 0 ? (
          listing.map((project) => {
            const PriorityIcon = getPriorityIcon(project.priority);
            const EvalStatusIcon = getEvalStatusIcon(project.eval_status || "");
            const EvalStatusBg = getEvalStatusBg(project.eval_status || "");
            const EvalStatusColor = getEvalStatusColor(project.eval_status || "");
            return (
              <Col key={project.project_id} xs={12} md={6} xl={4}>
                <div className={`h-100 border-0 rounded-4 overflow-hidden transition-all card shadow-sm`}>
                  <div
                    className="w-100"
                    style={{
                      height: "4px",
                      background: gradients.topBar,
                    }}
                  />
                  <div className="card-body p-4 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`tooltip-project-name`}> {project.name} </Tooltip>}
                        >
                          <h5 className="card-title mb-0 text-dark project-name">
                            <Link to={`/manage-project/${project.project_id}/${location?.search}`} state={{ from: location.state?.from || location.pathname, }} className="text-decoration-none text-dark"> {project.name}</Link>
                          </h5>
                        </OverlayTrigger>
                        <Badge
                          pill
                          className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0"
                          style={{
                            background: EvalStatusBg,
                            color: EvalStatusColor,
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <EvalStatusIcon className="me-2" size={15} />
                          {project.eval_status}
                        </Badge>
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2 w-100 mb-5">
                        {project.district_name && <div
                          className="text-dark badge border-0 rounded-pill px-3 py-2 fw-medium d-flex align-items-center fw-semibold"
                          style={{
                            background: gradients.lightBlue,
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="bi bi-geo-alt-fill me-1 text-primary flex-shrink-0"></i> {project.district_name}
                        </div>}
                        {project.priority && <Badge
                          className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0 text-white"
                          pill
                          style={{
                            background: gradients.badge,
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <PriorityIcon className="me-2" size={15} />
                          {project.priority}
                        </Badge>}
                      </div>
                    </div>
                    <div className="d-flex w-100 gap-3 mb-3">
                      {project.sector_name && InfoItem({ icon: "bi bi-building", value: project.sector_name })}
                    </div>
                    <PermissionGate allowed={can('project_evaluation_tool', 'view')} className="d-block">
                      <Link
                        to={`/pet/${project.project_id}?return_to=${encodeURIComponent(window.location.pathname)}`}
                        state={{ from: location.state?.from || location.pathname, }}
                        className="text-decoration-none d-block"
                      >
                        <div className="stage-gate-card border-0 rounded-4 p-3 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
                            <div
                              className="text-white rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: "25px",
                                height: "25px",
                                background: gradients.iconBg,
                              }}
                            >
                              <i className="bi bi-eye-fill fs-6 text-white"></i>
                            </div>

                            <div className="min-w-0">
                              <h6 className="fw-semibold mb-0 text-primary">
                                View Details
                              </h6>
                            </div>
                          </div>

                          <div className="arrow-circle flex-shrink-0 ms-2">
                            <i className="bi bi-chevron-right text-primary fs-5" ></i>
                          </div>
                        </div>
                      </Link>
                    </PermissionGate>
                  </div>
                </div>
              </Col>
            );
          })
        ) : (
          <div className="text-center text-muted">
            No projects found.
          </div>
        ))}

      </Row>
      {renderLoadMoreButton()}
    </>
  )
}
export default EvaluatorGridView;